import { useCallback } from 'react'
import { usePaywallState } from '@renderer/chat/store/paywalls'
import { useUnifiedBilling } from '@renderer/chat/store/global-billing'
import { isLegacyPricingPlan, isPlanEnded, topUpTier } from '@renderer/chat/utils/billingUtils'
import { useRequestSidepanelPaywall } from '@renderer/overlay/Sidepanel/components/sidepanel/sidepanel-paywall-request'
import type { TopUpKind } from './top-up-kinds'

/**
 * Shows the paywall for a refused feature, from any window.
 *
 * A tier is sold the top-up that refills what ran out. A free or lapsed plan
 * is sold the plan itself: sidepanel home docks its promo on the same request.
 *
 * A legacy pricing plan is sold nothing. No top-up can bill it, home docks no
 * promo for it (see sellsPlan), and the promo's only CTA switches plans, which
 * bills immediately when a card is on file: a $20 account would jump to $60
 * from a button that says Add Credits.
 */
export function useShowPaywall() {
  const paywall = usePaywallState()
  const { subscription } = useUnifiedBilling()
  const requestPaywall = useRequestSidepanelPaywall()

  return useCallback(
    (kind: TopUpKind) => {
      const tier = topUpTier({ plan: paywall.plan, planEnded: isPlanEnded(subscription) })
      if (!tier && isLegacyPricingPlan(paywall.plan)) return
      requestPaywall(kind)
    },
    [paywall.plan, subscription, requestPaywall],
  )
}
